import { useState, useMemo, useEffect, useRef } from "react"
import { ChevronDown, ChevronUp, Crown, Lock } from "lucide-react"
import { BracketTree } from "../components/BracketTree"
import { InfoModal } from "../components/portfolio/InfoModal"
import { useBracket } from "../hooks/useBracket"
import { useScoringRules } from "../hooks/useScoringRules"
import { useCompetitionRounds } from "../hooks/useCompetitionRounds"
import { useScenario } from "../hooks/useScenario"
import { Button, StatusMessage } from "../components/ui"
import type { PlayoffBracket, BracketRound, BracketMatchup, BracketTeam } from "../types/bracket"
import {
  isFootballTradingCompetition,
  isSoccerTradingCompetition,
} from "@sportex100/shared/utils/competitionIdentity.ts"
import {
  buildSoccerSettlementRows,
  type SoccerSettlementRowKey,
} from "@sportex100/shared/utils/soccerSettlementPoints.ts"
import {
  buildWorldCupBestThirdOrder,
  buildWorldCupBracketWithSimulatedKnockout,
  buildWorldCupGroupStagePanels,
  buildWorldCupGroupStageQualifications,
  countWorldCupGroupStageSelections,
  getWorldCupGroupStageQualificationSummary,
  normalizeWorldCupBestThirdRanks,
  resolveWorldCupBestThirdTrayCollapsed,
  WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP,
  WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS,
  WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS,
  WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS_PER_GROUP,
  type WorldCupGroupStageQualification,
  type WorldCupGroupStagePanel,
  type WorldCupGroupStageTeamPick,
} from "@sportex100/shared/utils/worldCupGroupStagePicks.ts"
import {
  isWorldCupCompetition,
  WORLD_CUP_ALL_GROUPS,
  type WorldCupGroupFilterValue,
} from "@sportex100/shared/utils/worldCupTeamFilter.ts"
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags"
import { useLocalization } from "../lib/i18n"
import { TeamCodeBadge } from "../components/ui/TeamCodeBadge"
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "../components/ui/teamIdentityScale"
import { cn } from "../lib/utils"
import { buildRoundGateState, type RoundGateState } from "../lib/picksRoundGate"
import { getWorldCupGroupStagePickCardStyle } from "../lib/teamPickStyles"

const WORLD_CUP_BEST_THIRD_TRAY_COLLAPSED_KEY = "picks.best_third_tray_collapsed.v1"

export type MatchupPrediction = {
  winnerTeamCode: string
  gamesWon: number // 4-7
  bestThirdRank?: number
}

export type PicksOrderPrefill = {
  side: "BUY" | "SELL"
  teamCode: string
  price?: number
  quantity?: number
}

type PicksPageProps = {
  competitionCode: string
  seasonCode: string
  poolCode: string | null
  onRefresh?: (refetch: () => void) => void
  onOpenOrderForm?: (prefill: PicksOrderPrefill) => void
}

type ScoringSummaryRow = {
  roundCode: string
  roundName: string
  roundSequence: number
  gameWinPoints: number
  roundWinPoints: number
  winsRequired: number
  seriesFormatName: string | null
  maxRoundPoints: number
}

type TranslateFn = ReturnType<typeof useLocalization>["t"]

type RoundFilterOption = {
  value: string
  label: string
  roundName: string
  locked: boolean
  complete: boolean
}

function formatPointValue(value: number) {
  return value.toLocaleString()
}

function formatCompetitionLabel(competitionCode: string) {
  return competitionCode.replace(/_/g, " ")
}

function formatSeriesLabel(
  seriesFormatName: string | null,
  winsRequired: number,
  translate: TranslateFn,
) {
  if (winsRequired <= 1) return translate("picks.series.singleGame")
  if (!seriesFormatName) return translate("picks.series.firstTo", { wins: winsRequired })
  const normalized = seriesFormatName.replace(/_/g, " ")
  return translate("picks.series.wins", { format: normalized, wins: winsRequired })
}

const SOCCER_SETTLEMENT_RANK_ICONS: Partial<
  Record<SoccerSettlementRowKey, { kind: "crown" | "rank"; label?: "2" | "3"; className: string }>
> = {
  winner: {
    kind: "crown",
    className: "text-warning",
  },
  runner_up: {
    kind: "rank",
    label: "2",
    className: "border-slate-300/80 bg-slate-200 text-background",
  },
  third_place: {
    kind: "rank",
    label: "3",
    className: "border-amber-700/80 bg-amber-600 text-background",
  },
}

function getSoccerSettlementDisplayLabel(
  rowKey: SoccerSettlementRowKey,
  translate: TranslateFn,
) {
  switch (rowKey) {
    case "winner":
      return translate("picks.pointSystem.soccer.winner")
    case "runner_up":
      return translate("picks.pointSystem.soccer.runnerUp")
    case "third_place":
      return translate("picks.pointSystem.soccer.thirdPlace")
    case "eliminated_semi_final":
      return translate("picks.pointSystem.soccer.eliminatedSemiFinal")
    case "eliminated_quarter_final":
      return translate("picks.pointSystem.soccer.eliminatedQuarterFinal")
    case "eliminated_round_16":
      return translate("picks.pointSystem.soccer.eliminatedRound16")
    case "eliminated_round_32":
      return translate("picks.pointSystem.soccer.eliminatedRound32")
    case "missed_knockout":
    default:
      return translate("picks.pointSystem.soccer.missedKnockout")
  }
}

function getSoccerSettlementRowClassNames(rowKey: SoccerSettlementRowKey) {
  switch (rowKey) {
    case "winner":
      return {
        rankIcon: SOCCER_SETTLEMENT_RANK_ICONS[rowKey],
        rowClassName:
          "grid min-h-[52px] grid-cols-[1fr_4rem] items-center border-b px-4 py-[14px]",
        labelClassName: "text-[14px] font-bold text-[#FFB020]",
        pointsClassName: "text-right font-display text-[22px] font-bold leading-none text-[#FFB020]",
        style: {
          background: "linear-gradient(90deg, rgba(245,166,35,0.18), rgba(245,166,35,0.04))",
          borderBottomColor: "rgba(255,176,32,0.15)",
        },
      }
    case "runner_up":
      return {
        rankIcon: SOCCER_SETTLEMENT_RANK_ICONS[rowKey],
        rowClassName:
          "grid min-h-[46px] grid-cols-[1fr_4rem] items-center border-b px-4 py-3",
        labelClassName: "text-[13px] font-semibold text-[#e8e6e1]",
        pointsClassName: "text-right font-display text-[18px] font-bold leading-none text-[#FFB020]",
        style: {
          background: "linear-gradient(90deg, rgba(245,166,35,0.10), transparent)",
          borderBottomColor: "rgba(255,255,255,0.04)",
        },
      }
    case "third_place":
      return {
        rankIcon: SOCCER_SETTLEMENT_RANK_ICONS[rowKey],
        rowClassName:
          "grid min-h-[46px] grid-cols-[1fr_4rem] items-center border-b px-4 py-3",
        labelClassName: "text-[13px] font-semibold text-[#e8e6e1]",
        pointsClassName: "text-right font-display text-[16px] font-bold leading-none text-[#FFB020]",
        style: {
          background: "linear-gradient(90deg, rgba(245,166,35,0.06), transparent)",
          borderBottomColor: "rgba(255,255,255,0.04)",
        },
      }
    case "eliminated_semi_final":
      return {
        rowClassName:
          "grid min-h-[40px] grid-cols-[1fr_4rem] items-center border-b px-4 py-[10px]",
        labelClassName: "text-[13px] font-medium text-[#a8b0bd]",
        pointsClassName:
          "text-right font-display text-[15px] font-bold leading-none text-[rgba(245,166,35,0.75)]",
        style: { borderBottomColor: "rgba(255,255,255,0.04)" },
      }
    case "eliminated_quarter_final":
      return {
        rowClassName:
          "grid min-h-[40px] grid-cols-[1fr_4rem] items-center border-b px-4 py-[10px]",
        labelClassName: "text-[13px] font-medium text-[#a8b0bd]",
        pointsClassName:
          "text-right font-display text-[15px] font-bold leading-none text-[rgba(245,166,35,0.60)]",
        style: { borderBottomColor: "rgba(255,255,255,0.04)" },
      }
    case "eliminated_round_16":
      return {
        rowClassName:
          "grid min-h-[40px] grid-cols-[1fr_4rem] items-center border-b px-4 py-[10px]",
        labelClassName: "text-[13px] font-medium text-[#8b93a1]",
        pointsClassName:
          "text-right font-display text-[14px] font-bold leading-none text-[rgba(245,166,35,0.45)]",
        style: { borderBottomColor: "rgba(255,255,255,0.04)" },
      }
    case "eliminated_round_32":
      return {
        rowClassName:
          "grid min-h-[40px] grid-cols-[1fr_4rem] items-center border-b px-4 py-[10px]",
        labelClassName: "text-[13px] font-medium text-[#8b93a1]",
        pointsClassName:
          "text-right font-display text-[14px] font-bold leading-none text-[rgba(245,166,35,0.45)]",
        style: { borderBottomColor: "rgba(255,255,255,0.04)" },
      }
    case "missed_knockout":
    default:
      return {
        rowClassName: "grid min-h-[40px] grid-cols-[1fr_4rem] items-center px-4 py-[10px]",
        labelClassName: "text-[13px] font-medium text-[#8b93a1]",
        pointsClassName:
          "text-right font-display text-[14px] font-bold leading-none text-[rgba(245,166,35,0.45)]",
        style: {},
      }
  }
}

function getProjectedTeamFromSource(
  sourceMatchup: BracketMatchup | undefined,
  sourcePrediction: MatchupPrediction | undefined,
  sourceOutcome: "WINNER" | "LOSER" | null | undefined,
): BracketTeam | null {
  if (!sourceMatchup) return null

  const resolvedWinnerTeamCode =
    sourcePrediction?.winnerTeamCode ?? sourceMatchup.winnerTeamCode
  if (!resolvedWinnerTeamCode) return null

  const higherSeed = sourceMatchup.higherSeed
  const lowerSeed = sourceMatchup.lowerSeed
  const winner = higherSeed?.teamCode === resolvedWinnerTeamCode ? higherSeed : lowerSeed
  const loser = higherSeed?.teamCode === resolvedWinnerTeamCode ? lowerSeed : higherSeed
  const projectedTeam = sourceOutcome === "LOSER" ? loser : winner

  return projectedTeam ? { ...projectedTeam, gamesWon: 0, gamesLost: 0 } : null
}

function WorldCupTeamFilter({
  selectedGroup,
  onSelectedGroupChange,
  t,
}: {
  selectedGroup: WorldCupGroupFilterValue
  onSelectedGroupChange: (group: WorldCupGroupFilterValue) => void
  t: TranslateFn
}) {
  const groupOptions: WorldCupGroupFilterValue[] = ["ALL", ...WORLD_CUP_ALL_GROUPS]

  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {groupOptions.map((group) => {
        const isSelected = selectedGroup === group
        return (
          <button
            key={group}
            type="button"
            onClick={() => onSelectedGroupChange(group)}
            className={cn(
              "min-h-8 min-w-9 shrink-0 rounded-[11px] px-2.5 py-1.5 font-display text-sm font-bold leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
              isSelected
                ? "bg-background-card text-brand shadow-[0_1px_0_rgba(255,255,255,0.08)]"
                : "text-foreground-faint hover:text-foreground",
            )}
            aria-pressed={isSelected}
          >
            {group === "ALL" ? t("market.worldCup.groupAll") : group}
          </button>
        )
      })}
    </div>
  )
}

function PicksRoundFilter({
  value,
  options,
  onChange,
  onAttemptLockedOption,
}: {
  value: string
  options: RoundFilterOption[]
  onChange: (value: string) => void
  onAttemptLockedOption: (value: string) => void
}) {
  if (options.length <= 1) return null

  return (
    <div className="picksRoundFilter -mx-1 overflow-x-auto px-1 pb-1">
      <div className="inline-flex min-w-full rounded-[14px] border border-border-subtle bg-background-raised p-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] md:min-w-0">
        {options.map((option) => {
          const isSelected = option.value === value
          const isLocked = option.locked
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                if (isLocked) {
                  onAttemptLockedOption(option.value)
                  return
                }
                onChange(option.value)
              }}
              className={cn(
                "min-h-8 min-w-[4.5rem] flex-1 rounded-[11px] px-2.5 py-1.5 font-display text-sm font-bold uppercase leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand md:min-w-[5.25rem]",
                isSelected
                  ? "bg-background-card text-foreground shadow-[0_1px_0_rgba(255,255,255,0.08)]"
                  : "text-foreground-faint hover:text-foreground",
                isLocked && "text-foreground-faint/55 hover:text-foreground-muted",
              )}
              aria-pressed={isSelected}
              aria-disabled={isLocked}
              aria-label={isLocked ? `${option.roundName} locked` : option.roundName}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {isLocked ? <Lock className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                {option.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function WorldCupGroupStagePanelCard({
  panel,
  predictions,
  teamScenarioPoints,
  onToggleTeam,
  onOpenOrderForm,
  t,
}: {
  panel: WorldCupGroupStagePanel<BracketTeam>
  predictions: Map<string, MatchupPrediction>
  teamScenarioPoints: Map<string, number>
  onToggleTeam: (
    panel: WorldCupGroupStagePanel<BracketTeam>,
    pick: WorldCupGroupStageTeamPick<BracketTeam>,
  ) => void
  onOpenOrderForm?: (prefill: PicksOrderPrefill) => void
  t: TranslateFn
}) {
  const panelRound: BracketRound = {
    roundCode: "GROUP_STAGE",
    roundName: "Group Stage",
    roundSequence: 1,
    winsRequired: 1,
    matchups: panel.teams.map((pick) => ({
      seriesCode: pick.seriesCode,
      seriesName: "Group Stage",
      higherSeed: pick.team,
      lowerSeed: null,
      higherSeedSourceSeriesCode: undefined,
      lowerSeedSourceSeriesCode: undefined,
      higherSeedSourceOutcome: undefined,
      lowerSeedSourceOutcome: undefined,
      higherSeedPlaceholderLabel: null,
      lowerSeedPlaceholderLabel: null,
      status: "ACTIVE",
      winnerTeamCode: null,
    })),
  }
  const qualifications = buildWorldCupGroupStageQualifications(panelRound, predictions)
  const qualificationBySeries = new Map(
    qualifications.map((qualification) => [qualification.seriesCode, qualification]),
  )
  const autoCount = qualifications.filter((qualification) => qualification.role === "AUTO").length
  const bestThirdCount = qualifications.filter(
    (qualification) => qualification.role === "BEST_THIRD",
  ).length

  return (
    <article className="rounded-2xl border border-border-subtle bg-background-card p-3 md:p-4">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">
            {t("picks.worldCup.qualifierPicks")}
          </p>
          <h3 className="font-display text-xl font-bold uppercase text-foreground">
            {t("picks.worldCup.groupTitle", { group: panel.group })}
          </h3>
        </div>
        <span className="rounded-full border border-brand/40 bg-brand/10 px-3 py-1 font-display text-sm font-bold text-brand">
          {autoCount}/{WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP}
          {bestThirdCount > 0 ? ` + ${bestThirdCount}` : ""}
        </span>
      </header>

      <div className="grid gap-2 sm:grid-cols-2">
        {panel.teams.map((pick) => {
          const isSelected = predictions.has(pick.seriesCode)
          const qualification = qualificationBySeries.get(pick.seriesCode) ?? null
          const qualifierRole = qualification?.role ?? null
          const bestThirdRankLabel =
            qualifierRole === "BEST_THIRD" && qualification?.bestThirdRank
              ? ` #${qualification.bestThirdRank}`
              : ""
          const pointsValue = teamScenarioPoints.get(`${pick.team.teamCode}_${pick.seriesCode}`) ?? 0
          const worldCupFlagCountryCode = getWorldCupFlagCountryCode(pick.team.teamCode)
          const handleWorldCupGroupStageValueClick = () => {
            onOpenOrderForm?.({
              side: "BUY",
              teamCode: pick.team.teamCode,
              price: pointsValue > 0 ? pointsValue : undefined,
            })
          }

          return (
            <div
              key={pick.seriesCode}
              className={cn(
                "grid min-h-[76px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 overflow-hidden rounded-xl border px-2.5 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                isSelected
                  ? "text-foreground"
                  : "text-foreground hover:border-brand/60",
              )}
              style={getWorldCupGroupStagePickCardStyle(isSelected)}
            >
              <button
                type="button"
                onClick={() => onToggleTeam(panel, pick)}
                className="contents text-left focus-visible:outline-none"
                aria-pressed={isSelected}
              >
                <TeamCodeBadge
                  code={pick.team.teamCode}
                  size={
                    worldCupFlagCountryCode
                      ? TEAM_IDENTITY_BADGE_SIZE.picker.worldCupBadge
                      : TEAM_IDENTITY_BADGE_SIZE.picker.badge
                  }
                  primaryColor={pick.team.teamPrimaryColor ?? null}
                  secondaryColor={pick.team.teamSecondaryColor ?? null}
                  variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                  flagCountryCode={worldCupFlagCountryCode}
                />
                <span className="min-w-0">
                  <span className={TEAM_IDENTITY_NAME_CLASS.pickerTile}>
                    {pick.team.teamName || pick.team.shortName || pick.team.teamCode}
                  </span>
                  <span className="block text-xs text-foreground-muted">
                    {t("picks.worldCup.seedGroupStage", { seed: pick.team.seed })}
                  </span>
                  {isSelected ? (
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span
                        className={cn(
                          "text-[10px] font-bold uppercase tracking-wide",
                          qualifierRole === "BEST_THIRD" ? "text-warning" : "text-brand",
                        )}
                      >
                        {qualifierRole === "BEST_THIRD"
                          ? `${t("picks.worldCup.bestThird")}${bestThirdRankLabel}`
                          : t("picks.worldCup.topTwo")}
                      </span>
                    </span>
                  ) : null}
                </span>
              </button>
              {isSelected ? (
                <button
                  type="button"
                  onClick={handleWorldCupGroupStageValueClick}
                  className="rounded-[6px] bg-[#FFB020] px-[9px] py-[3px] text-xs font-bold leading-none tabular-nums text-black transition hover:bg-[#ffc14d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-label={t("picks.actions.tradeTeam", {
                    team: pick.team.teamName || pick.team.shortName || pick.team.teamCode,
                  })}
                >
                  +{formatPointValue(pointsValue)}
                </button>
              ) : (
                <span
                  className="h-7 w-7 rounded-full border border-border-subtle bg-background"
                  aria-hidden="true"
                />
              )}
            </div>
          )
        })}
      </div>
    </article>
  )
}

function WorldCupBestThirdOrderTray({
  qualifications,
  collapsed,
  onToggleCollapsed,
  onMove,
  t,
}: {
  qualifications: WorldCupGroupStageQualification<BracketTeam>[]
  collapsed: boolean
  onToggleCollapsed: () => void
  onMove: (seriesCode: string, direction: -1 | 1) => void
  t: TranslateFn
}) {
  if (qualifications.length === 0) return null

  return (
    <div className="mx-[-0.75rem] mt-3 rounded-xl border border-border-subtle bg-background p-2 md:mx-[-1rem]">
      <button
        type="button"
        className="flex min-h-[46px] w-full items-center justify-between gap-3 rounded-lg px-1 text-left transition hover:bg-background-raised/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
      >
        <span className="min-w-0">
          <span className="block truncate font-display text-sm font-bold uppercase tracking-[0.08em] text-foreground-muted">
            {t("picks.worldCup.bestThirdOrder")}
          </span>
        </span>
        {collapsed ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-foreground-muted" aria-hidden="true" />
        ) : (
          <ChevronUp className="h-5 w-5 shrink-0 text-foreground-muted" aria-hidden="true" />
        )}
      </button>
      {!collapsed && (
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {qualifications.map((qualification, index) => {
            const teamName =
              qualification.team.teamName ||
              qualification.team.shortName ||
              qualification.team.teamCode
            const worldCupFlagCountryCode = getWorldCupFlagCountryCode(qualification.team.teamCode)
            return (
              <div
                key={qualification.seriesCode}
                className="grid min-h-[46px] grid-cols-[1.5rem_auto_minmax(0,1fr)_auto] items-center gap-2 rounded-lg border border-info/30 bg-info/10 px-2 py-1.5"
              >
                <span className="text-center font-mono text-sm font-bold tabular-nums text-info">
                  {index + 1}
                </span>
                <TeamCodeBadge
                  code={qualification.team.teamCode}
                  size={32}
                  primaryColor={qualification.team.teamPrimaryColor ?? null}
                  secondaryColor={qualification.team.teamSecondaryColor ?? null}
                  variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                  flagCountryCode={worldCupFlagCountryCode}
                />
                <span className="min-w-0">
                  <span className="block truncate font-display text-xs font-bold uppercase tracking-wide text-foreground">
                    {teamName}
                  </span>
                  <span className="block text-[11px] text-foreground-muted">
                    {t("picks.worldCup.groupTitle", { group: qualification.group })}
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onMove(qualification.seriesCode, -1)}
                    disabled={index === 0}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border-subtle bg-background-raised text-foreground transition hover:border-info/60 disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label={t("picks.worldCup.bestThirdMoveUp", { team: teamName })}
                  >
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(qualification.seriesCode, 1)}
                    disabled={index === qualifications.length - 1}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border-subtle bg-background-raised text-foreground transition hover:border-info/60 disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label={t("picks.worldCup.bestThirdMoveDown", { team: teamName })}
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function PicksPage({ competitionCode, seasonCode, poolCode, onRefresh, onOpenOrderForm }: PicksPageProps) {
  const { t } = useLocalization()
  const { bracket, loading, error, refetch } = useBracket(competitionCode, seasonCode)
  const { rules, pointsPerRound } = useScoringRules(competitionCode, seasonCode)
  const { rounds: competitionRounds, winsRequired: winsRequiredMap } = useCompetitionRounds()
  const [scenarioMode] = useState(true) // Always in scenario mode now
  const [predictions, setPredictions] = useState<Map<string, MatchupPrediction>>(new Map())
  const [worldCupGroupFilter, setWorldCupGroupFilter] =
    useState<WorldCupGroupFilterValue>("ALL")
  const [worldCupBestThirdTrayPreference, setWorldCupBestThirdTrayPreference] =
    useState<boolean | null>(null)
  const [selectedRoundCode, setSelectedRoundCode] = useState("")
  const [showPointSystem, setShowPointSystem] = useState(false)
  const lastScenarioLoadKeyRef = useRef("")
  const isWorldCupPicks = isWorldCupCompetition(competitionCode)
  const worldCupGroupStageRound = useMemo(
    () => bracket?.rounds.find((round) => round.roundCode === "GROUP_STAGE") ?? null,
    [bracket],
  )
  
  // Scenario persistence hook
  const { loadScenario, saveScenario, deleteScenario, isLoading: isLoadingScenario } = useScenario({
    poolCode,
    enabled: true
  })

  // Create a modified bracket with predicted winners advancing to next rounds
  // Helper function to get predicted winners from a round
  const getPredictedWinnersFromRound = (
    roundMatchups: BracketMatchup[],
    roundPredictions: Map<string, MatchupPrediction>
  ): BracketTeam[] => {
    const winners: BracketTeam[] = []
    roundMatchups.forEach(matchup => {
      const prediction = roundPredictions.get(matchup.seriesCode)
      if (prediction) {
        const winner = matchup.higherSeed?.teamCode === prediction.winnerTeamCode
          ? matchup.higherSeed
          : matchup.lowerSeed
        if (winner) {
          winners.push({ ...winner, gamesWon: 0, gamesLost: 0 })
        }
      }
    })
    return winners
  }

  // Football-style reseeding: #1 seed plays lowest remaining, others play each other
  const applyNflReseeding = (
    byeTeam: BracketTeam | null,
    wildcardWinners: BracketTeam[]
  ): { div1Higher: BracketTeam | null; div1Lower: BracketTeam | null; div2Higher: BracketTeam | null; div2Lower: BracketTeam | null } => {
    // Sort winners by seed (ascending = higher seed first)
    const sortedWinners = [...wildcardWinners].sort((a, b) => a.seed - b.seed)
    
    if (sortedWinners.length < 3) {
      // Not enough winners yet
      return { div1Higher: byeTeam, div1Lower: null, div2Higher: null, div2Lower: null }
    }
    
    // #1 seed (bye) plays the LOWEST remaining seed (highest seed number)
    const lowestSeed = sortedWinners[sortedWinners.length - 1] // Highest seed number = lowest ranked
    
    // Other two winners play each other
    const otherWinners = sortedWinners.slice(0, 2) // First two (higher seeds)
    
    return {
      div1Higher: byeTeam,
      div1Lower: lowestSeed,
      div2Higher: otherWinners[0] || null, // Higher seed
      div2Lower: otherWinners[1] || null   // Lower seed
    }
  }

  const bracketWithSimulatedKnockout = useMemo(
    () =>
      buildWorldCupBracketWithSimulatedKnockout(
        bracket,
        predictions,
        competitionCode,
      ) as PlayoffBracket | null,
    [bracket, competitionCode, predictions],
  )

  const bracketWithPredictions = useMemo((): PlayoffBracket | null => {
    if (!bracketWithSimulatedKnockout || !scenarioMode || predictions.size === 0) {
      return bracketWithSimulatedKnockout
    }

    // Sort rounds inside memo to avoid dependency issues
    const roundsSorted = [...bracketWithSimulatedKnockout.rounds].sort((a, b) => a.roundSequence - b.roundSequence)
    
    const isNfl = isFootballTradingCompetition(competitionCode)
    
    // Build rounds progressively so each round can reference the previous modified round
    const modifiedRounds: BracketRound[] = []
    const modifiedMatchupBySeries = new Map<string, BracketMatchup>()
    
    roundsSorted.forEach((round, roundIndex) => {
      const modifiedMatchups = round.matchups.map(matchup => {
        let higherSeed = matchup.higherSeed
        let lowerSeed = matchup.lowerSeed
        const canProjectParticipants = matchup.status === 'PENDING'

        // Football-specific reseeding logic for later rounds
        // Only apply reseeding if we have predictions for the source rounds
        if (canProjectParticipants && isNfl && roundIndex > 0) {
          const previousRound = modifiedRounds[roundIndex - 1]

          if (round.roundCode === 'DIVISIONAL' && previousRound) {
            // Get Wild Card winners for this conference
            const conference = matchup.seriesCode.startsWith('AFC') ? 'AFC' : 'NFC'
            const confWildcardMatchups = previousRound.matchups.filter(m => m.seriesCode.startsWith(conference))
            const wildcardWinners = getPredictedWinnersFromRound(confWildcardMatchups, predictions)
            
            // Only apply reseeding if we have predictions for ALL wild card matchups in this conference
            if (wildcardWinners.length === confWildcardMatchups.length && confWildcardMatchups.length > 0) {
              // Find the bye team (#1 seed) - it's the higherSeed of DIV-1 matchup
              const div1Matchup = round.matchups.find(m => m.seriesCode === `${conference}-DIV-1`)
              const byeTeam = div1Matchup?.higherSeed || null
              
              const reseeded = applyNflReseeding(byeTeam, wildcardWinners)
              
              if (matchup.seriesCode === `${conference}-DIV-1`) {
                higherSeed = reseeded.div1Higher
                lowerSeed = reseeded.div1Lower
              } else if (matchup.seriesCode === `${conference}-DIV-2`) {
                higherSeed = reseeded.div2Higher
                lowerSeed = reseeded.div2Lower
              }
            }
            // Otherwise keep original higherSeed/lowerSeed from the matchup
          } else if (round.roundCode === 'CONF_CHAMP' && previousRound) {
            // Semi-finals: winners from the previous round
            const conference = matchup.seriesCode.startsWith('AFC') ? 'AFC' : 'NFC'
            const confDivMatchups = previousRound.matchups.filter(m => m.seriesCode.startsWith(conference))
            const divWinners = getPredictedWinnersFromRound(confDivMatchups, predictions)
            
            // Only apply if we have predictions for ALL divisional matchups in this conference
            if (divWinners.length === confDivMatchups.length && confDivMatchups.length > 0) {
              // Sort by seed - higher seed is home
              const sortedDivWinners = [...divWinners].sort((a, b) => a.seed - b.seed)
              higherSeed = sortedDivWinners[0] || null
              lowerSeed = sortedDivWinners[1] || null
            }
            // Otherwise keep original higherSeed/lowerSeed from the matchup
          } else if (round.roundCode === 'SUPER_BOWL' && previousRound) {
            // Final: winners from the semi-final rounds
            const confChampWinners = getPredictedWinnersFromRound(previousRound.matchups, predictions)
            
            // Only apply if we have predictions for BOTH conference championship matchups
            if (confChampWinners.length === previousRound.matchups.length && previousRound.matchups.length > 0) {
              // Sort by seed - higher seed (lower number) first
              const sortedConfWinners = [...confChampWinners].sort((a, b) => a.seed - b.seed)
              higherSeed = sortedConfWinners[0] || null
              lowerSeed = sortedConfWinners[1] || null
            }
            // Otherwise keep original higherSeed/lowerSeed from the matchup
          }
        }
        // Standard bracket progression with fixed source series
        else if (canProjectParticipants && roundIndex > 0 && (matchup.higherSeedSourceSeriesCode || matchup.lowerSeedSourceSeriesCode)) {
          // Use the already-modified previous round
          // Check if higher seed should come from a prediction
          if (matchup.higherSeedSourceSeriesCode) {
            const sourcePrediction = predictions.get(matchup.higherSeedSourceSeriesCode)
            const sourceMatchup = modifiedMatchupBySeries.get(matchup.higherSeedSourceSeriesCode)
            const projectedTeam = getProjectedTeamFromSource(
              sourceMatchup,
              sourcePrediction,
              matchup.higherSeedSourceOutcome,
            )
            if (projectedTeam) {
              higherSeed = projectedTeam
            }
          }

          // Check if lower seed should come from a prediction
          if (matchup.lowerSeedSourceSeriesCode) {
            const sourcePrediction = predictions.get(matchup.lowerSeedSourceSeriesCode)
            const sourceMatchup = modifiedMatchupBySeries.get(matchup.lowerSeedSourceSeriesCode)
            const projectedTeam = getProjectedTeamFromSource(
              sourceMatchup,
              sourcePrediction,
              matchup.lowerSeedSourceOutcome,
            )
            if (projectedTeam) {
              lowerSeed = projectedTeam
            }
          }
        }

        // Apply prediction for this matchup itself
        const prediction = predictions.get(matchup.seriesCode)
        const isComplete = matchup.status === 'COMPLETE'
        if (prediction && !isComplete) {
          const winnerTeamCode = higherSeed?.teamCode === prediction.winnerTeamCode
            ? higherSeed.teamCode
            : lowerSeed?.teamCode || null

          return {
            ...matchup,
            higherSeed,
            lowerSeed,
            winnerTeamCode,
            status: matchup.status // Keep it as predicted, not actually complete
          }
        }

        return {
          ...matchup,
          higherSeed,
          lowerSeed,
        }
      })
      
      const modifiedRound = {
        ...round,
        matchups: modifiedMatchups
      }
      modifiedRounds.push(modifiedRound)
      modifiedMatchups.forEach((matchup) => {
        modifiedMatchupBySeries.set(matchup.seriesCode, matchup)
      })
    })

    return {
      ...bracketWithSimulatedKnockout,
      rounds: modifiedRounds
    }
  }, [bracketWithSimulatedKnockout, scenarioMode, predictions, competitionCode])

  // Calculate team points based on predictions AND actual results
  const teamScenarioPoints = useMemo(() => {
    if (!bracketWithPredictions || pointsPerRound.length === 0) return new Map<string, number>()

    const pointsByTeamAndSeries = new Map<string, number>()
    const modifiedRounds = bracketWithPredictions.rounds
      .slice()
      .sort((a, b) => a.roundSequence - b.roundSequence)

    // Track when each team first appears so we can grant skipped-round bonuses (bye weeks)
    const firstAppearance = new Map<string, number>()
    modifiedRounds.forEach((round, roundIndex) => {
      round.matchups.forEach(matchup => {
        if (matchup.higherSeed && !firstAppearance.has(matchup.higherSeed.teamCode)) {
          firstAppearance.set(matchup.higherSeed.teamCode, roundIndex)
        }
        if (matchup.lowerSeed && !firstAppearance.has(matchup.lowerSeed.teamCode)) {
          firstAppearance.set(matchup.lowerSeed.teamCode, roundIndex)
        }
      })
    })

    const teamBaseBonus = new Map<string, number>()
    firstAppearance.forEach((roundIndex, teamCode) => {
      if (roundIndex > 0) {
        const bonus = pointsPerRound
          .slice(0, roundIndex)
          .reduce((sum, roundPoints) => sum + (roundPoints?.roundBonus ?? 0), 0)

        if (bonus > 0) {
          teamBaseBonus.set(teamCode, bonus)
        }
      }
    })

    const cumulativePoints = new Map<string, number>()
    const ensureTeamSeed = (team: BracketTeam | null, seriesCode: string) => {
      if (!team) return
      if (!cumulativePoints.has(team.teamCode)) {
        const startingPoints = teamBaseBonus.get(team.teamCode) || 0
        cumulativePoints.set(team.teamCode, startingPoints)

        if (startingPoints > 0 && !pointsByTeamAndSeries.has(`${team.teamCode}_${seriesCode}`)) {
          pointsByTeamAndSeries.set(`${team.teamCode}_${seriesCode}`, startingPoints)
        }
      }
    }

    modifiedRounds.forEach((round, roundIndex) => {
      const roundPoints = pointsPerRound[roundIndex]
      if (!roundPoints) return

      // Use wins_required from competition config
      const winsRequired = winsRequiredMap[round.roundCode] ?? round.winsRequired ?? 1

      round.matchups.forEach(matchup => {
        ensureTeamSeed(matchup.higherSeed, matchup.seriesCode)
        ensureTeamSeed(matchup.lowerSeed, matchup.seriesCode)
        const seedSeriesCarryover = (team: BracketTeam | null, seriesCode: string) => {
          if (!team) return
          const key = `${team.teamCode}_${seriesCode}`
          if (pointsByTeamAndSeries.has(key)) return
          const currentTotal = cumulativePoints.get(team.teamCode) ?? 0
          pointsByTeamAndSeries.set(key, currentTotal)
        }
        seedSeriesCarryover(matchup.higherSeed, matchup.seriesCode)
        seedSeriesCarryover(matchup.lowerSeed, matchup.seriesCode)

        const prediction = predictions.get(matchup.seriesCode)

        if (matchup.status === 'COMPLETE' && matchup.winnerTeamCode) {
          const winnerTeamCode = matchup.winnerTeamCode
          const loserTeam = matchup.higherSeed?.teamCode === winnerTeamCode
            ? matchup.lowerSeed
            : matchup.higherSeed
          
          // Determine winner games won: if available in team object, use it; else assume winsRequired
          const winnerTeamObj = matchup.higherSeed?.teamCode === winnerTeamCode ? matchup.higherSeed : matchup.lowerSeed
          const winnerGamesWon = winnerTeamObj?.gamesWon ?? winsRequired

          const winnerPrev = cumulativePoints.get(winnerTeamCode) ?? 0
          const winnerAward = (winnerGamesWon * roundPoints.gameWin) + roundPoints.roundBonus
          const winnerTotal = winnerPrev + winnerAward
          cumulativePoints.set(winnerTeamCode, winnerTotal)
          pointsByTeamAndSeries.set(`${winnerTeamCode}_${matchup.seriesCode}`, winnerTotal)

          if (loserTeam?.teamCode) {
            const loserPrev = cumulativePoints.get(loserTeam.teamCode) ?? 0
            const loserGamesWon = loserTeam.gamesWon ?? 0
            const loserAward = loserGamesWon * roundPoints.gameWin
            const loserTotal = loserPrev + loserAward
            cumulativePoints.set(loserTeam.teamCode, loserTotal)
            pointsByTeamAndSeries.set(`${loserTeam.teamCode}_${matchup.seriesCode}`, loserTotal)
          }
        } else if (prediction) {
          const { winnerTeamCode, gamesWon } = prediction
          const loserTeamCode = matchup.higherSeed?.teamCode === winnerTeamCode
            ? matchup.lowerSeed?.teamCode
            : matchup.higherSeed?.teamCode

          const winnerPrev = cumulativePoints.get(winnerTeamCode) ?? 0
          const winnerAward = (winsRequired * roundPoints.gameWin) + roundPoints.roundBonus
          const winnerTotal = winnerPrev + winnerAward
          cumulativePoints.set(winnerTeamCode, winnerTotal)
          pointsByTeamAndSeries.set(`${winnerTeamCode}_${matchup.seriesCode}`, winnerTotal)

          if (loserTeamCode) {
            const loserPrev = cumulativePoints.get(loserTeamCode) ?? 0
            const loserGamesWon = Math.max(0, gamesWon - winsRequired)
            const loserAward = loserGamesWon * roundPoints.gameWin
            const loserTotal = loserPrev + loserAward
            cumulativePoints.set(loserTeamCode, loserTotal)
            pointsByTeamAndSeries.set(`${loserTeamCode}_${matchup.seriesCode}`, loserTotal)
          }
        } else if (matchup.status !== 'PENDING') {
          // No user pick yet: reflect current live series progress (banked game wins only).
          if (matchup.higherSeed?.teamCode) {
            const higherPrev = cumulativePoints.get(matchup.higherSeed.teamCode) ?? 0
            const higherGamesWon = matchup.higherSeed.gamesWon ?? 0
            const higherTotal = higherPrev + higherGamesWon * roundPoints.gameWin
            cumulativePoints.set(matchup.higherSeed.teamCode, higherTotal)
            pointsByTeamAndSeries.set(`${matchup.higherSeed.teamCode}_${matchup.seriesCode}`, higherTotal)
          }
          if (matchup.lowerSeed?.teamCode) {
            const lowerPrev = cumulativePoints.get(matchup.lowerSeed.teamCode) ?? 0
            const lowerGamesWon = matchup.lowerSeed.gamesWon ?? 0
            const lowerTotal = lowerPrev + lowerGamesWon * roundPoints.gameWin
            cumulativePoints.set(matchup.lowerSeed.teamCode, lowerTotal)
            pointsByTeamAndSeries.set(`${matchup.lowerSeed.teamCode}_${matchup.seriesCode}`, lowerTotal)
          }
        }
      })
    })

    return pointsByTeamAndSeries
  }, [bracketWithPredictions, predictions, pointsPerRound, winsRequiredMap])

  const handlePredictionChange = (seriesCode: string, prediction: MatchupPrediction | null) => {
    setPredictions(prev => {
      const next = new Map(prev)
      if (prediction) {
        next.set(seriesCode, prediction)
      } else {
        next.delete(seriesCode)
      }
      return isWorldCupPicks
        ? normalizeWorldCupBestThirdRanks(worldCupGroupStageRound, next)
        : next
    })
  }

  const visibleRounds = useMemo(
    () => bracketWithPredictions?.rounds ?? bracket?.rounds ?? [],
    [bracket?.rounds, bracketWithPredictions?.rounds],
  )
  const roundGateState = useMemo<RoundGateState[]>(
    () => buildRoundGateState(visibleRounds, predictions),
    [predictions, visibleRounds],
  )
  const roundFilterOptions = useMemo<RoundFilterOption[]>(
    () =>
      roundGateState.map((state) => ({
        value: state.roundCode,
        label: state.roundCode === "GROUP_STAGE" ? t("picks.round.groupStage") : state.roundLabel,
        roundName: state.roundCode === "GROUP_STAGE" ? t("picks.round.groupStage") : state.roundName,
        locked: !state.isUnlocked,
        complete: state.isComplete,
      })),
    [roundGateState, t],
  )
  const selectedRound = useMemo(
    () =>
      visibleRounds.find((round) => round.roundCode === selectedRoundCode) ??
      visibleRounds[0] ??
      null,
    [selectedRoundCode, visibleRounds],
  )
  const groupStageRound = useMemo(
    () => visibleRounds.find((round) => round.roundCode === "GROUP_STAGE") ?? null,
    [visibleRounds],
  )
  const worldCupGroupStageSummary = useMemo(() => {
    if (!isWorldCupPicks || selectedRound?.roundCode !== "GROUP_STAGE" || !groupStageRound) {
      return null
    }
    return getWorldCupGroupStageQualificationSummary(groupStageRound, predictions)
  }, [groupStageRound, isWorldCupPicks, predictions, selectedRound?.roundCode])
  const worldCupBestThirdTrayCollapsed = useMemo(
    () =>
      resolveWorldCupBestThirdTrayCollapsed(
        worldCupGroupStageSummary,
        worldCupBestThirdTrayPreference,
      ),
    [worldCupBestThirdTrayPreference, worldCupGroupStageSummary],
  )
  const worldCupBestThirdOrder = useMemo(
    () =>
      isWorldCupPicks && selectedRound?.roundCode === "GROUP_STAGE" && groupStageRound
        ? buildWorldCupBestThirdOrder(groupStageRound, predictions)
        : [],
    [groupStageRound, isWorldCupPicks, predictions, selectedRound?.roundCode],
  )
  const worldCupGroupStagePanels = useMemo(
    () =>
      isWorldCupPicks && selectedRound?.roundCode === "GROUP_STAGE" && groupStageRound
        ? buildWorldCupGroupStagePanels(groupStageRound).filter(
            (panel) => worldCupGroupFilter === "ALL" || panel.group === worldCupGroupFilter,
          )
        : [],
    [groupStageRound, isWorldCupPicks, selectedRound?.roundCode, worldCupGroupFilter],
  )
  const selectedBracketTreeRounds = useMemo(
    () =>
      selectedRound && !(isWorldCupPicks && selectedRound.roundCode === "GROUP_STAGE")
        ? [selectedRound]
        : [],
    [isWorldCupPicks, selectedRound],
  )

  const showWorldCupGroupStagePanels =
    isWorldCupPicks && selectedRound?.roundCode === "GROUP_STAGE" && worldCupGroupStagePanels.length > 0

  useEffect(() => {
    try {
      const value = window.localStorage.getItem(WORLD_CUP_BEST_THIRD_TRAY_COLLAPSED_KEY)
      if (value === "1") {
        setWorldCupBestThirdTrayPreference(true)
      } else if (value === "0") {
        setWorldCupBestThirdTrayPreference(false)
      }
    } catch {
      // Ignore storage failures; the default collapsed state still works.
    }
  }, [])

  useEffect(() => {
    if (visibleRounds.length === 0) {
      setSelectedRoundCode("")
      return
    }
    setSelectedRoundCode((current) => {
      if (current && visibleRounds.some((round) => round.roundCode === current)) {
        return current
      }
      return visibleRounds[0].roundCode
    })
  }, [visibleRounds])

  useEffect(() => {
    if (!selectedRoundCode) return
    const selectedRoundState = roundGateState.find((state) => state.roundCode === selectedRoundCode)
    if (!selectedRoundState || selectedRoundState.isUnlocked) return
    const requiredRoundState = selectedRoundState.requiredRoundCode
      ? roundGateState.find((state) => state.roundCode === selectedRoundState.requiredRoundCode)
      : null
    const firstUnlockedRound = roundGateState.find((state) => state.isUnlocked)
    const redirectRoundState = requiredRoundState ?? firstUnlockedRound
    if (redirectRoundState) {
      setSelectedRoundCode(redirectRoundState.roundCode)
    }
  }, [roundGateState, selectedRoundCode])

  const handleLockedRoundAttempt = (roundCode: string) => {
    const targetRoundState = roundGateState.find((state) => state.roundCode === roundCode)
    if (!targetRoundState || targetRoundState.isUnlocked) return

    const lastUnlockedBeforeTarget = [...roundGateState]
      .filter((state) => state.isUnlocked && state.index < targetRoundState.index)
      .sort((a, b) => b.index - a.index)[0]
    const fallbackUnlockedRound = roundGateState.find((state) => state.isUnlocked)
    const redirectRoundState = lastUnlockedBeforeTarget ?? fallbackUnlockedRound ?? null
    const requiredRoundName =
      redirectRoundState?.roundName ??
      targetRoundState.requiredRoundName ??
      t("picks.alert.previousRound")

    window.alert(
      `${t("picks.alert.lockedTitle", { round: targetRoundState.roundName })}\n${t(
        "picks.alert.lockedMessage",
        { requiredRound: requiredRoundName },
      )}`,
    )
    if (redirectRoundState) {
      setSelectedRoundCode(redirectRoundState.roundCode)
    }
  }

  const handleRoundFilterChange = (roundCode: string) => {
    const targetRoundState = roundGateState.find((state) => state.roundCode === roundCode)
    if (!targetRoundState) return
    if (!targetRoundState.isUnlocked) {
      handleLockedRoundAttempt(roundCode)
      return
    }
    setSelectedRoundCode(roundCode)
  }

  const handleWorldCupGroupStageToggle = (
    panel: WorldCupGroupStagePanel<BracketTeam>,
    pick: WorldCupGroupStageTeamPick<BracketTeam>,
  ) => {
    const isSelected = predictions.has(pick.seriesCode)
    if (!isSelected) {
      const groupSelectedCount = panel.teams.filter((teamPick) =>
        predictions.has(teamPick.seriesCode),
      ).length
      if (groupSelectedCount >= WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS_PER_GROUP) {
        window.alert(
          `${t("picks.alert.groupLimitTitle")}\n${t("picks.alert.groupLimitMessage", {
            count: WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS_PER_GROUP,
          })}`,
        )
        return
      }

      const totalSelectedCount = countWorldCupGroupStageSelections(predictions)
      if (totalSelectedCount >= WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS) {
        window.alert(
          `${t("picks.alert.qualifierLimitTitle")}\n${t("picks.alert.qualifierLimitMessage", {
            count: WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS,
          })}`,
        )
        return
      }

      if (groupSelectedCount >= WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP) {
        const summary = getWorldCupGroupStageQualificationSummary(groupStageRound, predictions)
        if (summary.bestThirdCount >= WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS) {
          window.alert(
            `${t("picks.alert.bestThirdLimitTitle")}\n${t("picks.alert.bestThirdLimitMessage", {
              count: WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS,
            })}`,
          )
          return
        }
      }
    }

    handlePredictionChange(
      pick.seriesCode,
      isSelected
        ? null
        : {
            winnerTeamCode: pick.team.teamCode,
            gamesWon: 1,
        },
    )
  }

  const handleWorldCupBestThirdMove = (seriesCode: string, direction: -1 | 1) => {
    setPredictions((prev) => {
      const normalized = normalizeWorldCupBestThirdRanks(worldCupGroupStageRound, prev)
      const order = buildWorldCupBestThirdOrder(worldCupGroupStageRound, normalized)
      const currentIndex = order.findIndex((qualification) => qualification.seriesCode === seriesCode)
      const nextIndex = currentIndex + direction
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= order.length) {
        return prev
      }

      const reordered = [...order]
      const [moved] = reordered.splice(currentIndex, 1)
      if (!moved) return prev
      reordered.splice(nextIndex, 0, moved)

      const next = new Map(normalized)
      reordered.forEach((qualification, index) => {
        const prediction = next.get(qualification.seriesCode)
        if (!prediction) return
        next.set(qualification.seriesCode, {
          ...prediction,
          bestThirdRank: index + 1,
        })
      })
      return next
    })
  }

  const handleWorldCupBestThirdTrayToggle = () => {
    setWorldCupBestThirdTrayPreference((currentPreference) => {
      const nextCollapsed = !resolveWorldCupBestThirdTrayCollapsed(
        worldCupGroupStageSummary,
        currentPreference,
      )
      try {
        window.localStorage.setItem(WORLD_CUP_BEST_THIRD_TRAY_COLLAPSED_KEY, nextCollapsed ? "1" : "0")
      } catch {
        // Ignore storage failures; keep the in-memory toggle.
      }
      return nextCollapsed
    })
  }

  const handleResetScenario = async () => {
    setPredictions(new Map())
    await deleteScenario()
  }

  // Load saved scenario on mount and merge with materialized results
  useEffect(() => {
    if (!poolCode || !bracket) {
      lastScenarioLoadKeyRef.current = ""
      return
    }

    const roundCount = bracket.rounds.length
    const matchupCount = bracket.rounds.reduce((total, round) => total + round.matchups.length, 0)
    const loadKey = `${poolCode}:${roundCount}:${matchupCount}`
    if (lastScenarioLoadKeyRef.current === loadKey) {
      return
    }

    let isMounted = true

    loadScenario().then(loadedPredictions => {
      if (!isMounted) return
      const merged = new Map(loadedPredictions || new Map())
      const loadedSize = merged.size
      
      // Add materialized series (completed series with actual results) as predictions
      // Only add if not already in saved predictions (don't overwrite user predictions)
      bracket.rounds.forEach(round => {
        round.matchups.forEach(matchup => {
          // If series is complete and has a winner, add it as a "prediction"
          if (matchup.status === 'COMPLETE' && matchup.winnerTeamCode && !merged.has(matchup.seriesCode)) {
            const winnerTeamCode = matchup.winnerTeamCode
            const gamesWon = matchup.higherSeed?.teamCode === winnerTeamCode
              ? matchup.higherSeed.gamesWon
              : matchup.lowerSeed?.gamesWon || 4
            
            merged.set(matchup.seriesCode, {
              winnerTeamCode: winnerTeamCode,
              gamesWon: gamesWon || 4
            })
          }
        })
      })
      
      const normalized = isWorldCupPicks
        ? normalizeWorldCupBestThirdRanks(worldCupGroupStageRound, merged)
        : merged
      console.log(`[PicksPage] Loaded ${loadedSize} predictions, merged to ${normalized.size} total (added ${merged.size - loadedSize} materialized)`)
      setPredictions(normalized)
      lastScenarioLoadKeyRef.current = loadKey
    }).catch(() => {
      if (!isMounted) return
      // Keep key clear on failure so user can retry via refresh.
      if (lastScenarioLoadKeyRef.current === loadKey) {
        lastScenarioLoadKeyRef.current = ""
      }
    })

    return () => {
      isMounted = false
    }
  }, [poolCode, bracket, loadScenario, isWorldCupPicks, worldCupGroupStageRound])

  // Auto-save when predictions change
  useEffect(() => {
    if (!poolCode || predictions.size === 0) return
    
    const timer = setTimeout(() => {
      console.log(`[PicksPage] Auto-saving ${predictions.size} predictions`)
      saveScenario(predictions)
    }, 1000) // Debounce by 1 second
    
    return () => clearTimeout(timer)
  }, [predictions, poolCode, saveScenario])

  useEffect(() => {
    if (onRefresh) {
      onRefresh(refetch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefresh])

  const scoringSummaryRows = useMemo<ScoringSummaryRow[]>(() => {
    if (!rules.length) return []
    const roundsByCode = new Map(
      competitionRounds.map((round) => [round.round_code, round]),
    )

    return [...rules]
      .sort((a, b) => a.round_sequence - b.round_sequence)
      .map((rule) => {
        const roundInfo = roundsByCode.get(rule.round_code)
        const winsRequired = roundInfo?.wins_required ?? winsRequiredMap[rule.round_code] ?? 1
        const maxRoundPoints = winsRequired * rule.game_win_points + rule.round_win_points
        return {
          roundCode: rule.round_code,
          roundName: rule.round_name,
          roundSequence: rule.round_sequence,
          gameWinPoints: rule.game_win_points,
          roundWinPoints: rule.round_win_points,
          winsRequired,
          seriesFormatName: roundInfo?.series_format_name ?? null,
          maxRoundPoints,
        }
      })
  }, [competitionRounds, rules, winsRequiredMap])

  const soccerSettlementRows = useMemo(() => buildSoccerSettlementRows(rules), [rules])
  const isSoccerPointSystem =
    isSoccerTradingCompetition(competitionCode) && soccerSettlementRows.length > 0
  const competitionLabel = useMemo(
    () => formatCompetitionLabel(competitionCode),
    [competitionCode],
  )
  const canShowPointSystem = isSoccerPointSystem
    ? soccerSettlementRows.length > 0
    : scoringSummaryRows.length > 0

  return (
    <div className="mx-auto flex w-full max-w-[64rem] flex-col gap-3 px-4 py-3">
      {loading && <StatusMessage>{t("picks.loading.bracket")}</StatusMessage>}
      {isLoadingScenario && <StatusMessage>{t("picks.loading.saved")}</StatusMessage>}
      {error && (
        <StatusMessage variant="error">
          {t("picks.error.loadFailed", { error })}
        </StatusMessage>
      )}

      {bracket && !loading && !error && (
        <section className="space-y-3">
          {selectedRound && (
            <div className="picksRoundHeader rounded-2xl border border-border-subtle bg-background-card p-3 shadow-sm md:p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-brand">
                    {t("picks.scenario.title")}
                  </p>
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h2 className="truncate font-display text-2xl font-bold uppercase leading-none text-foreground md:text-3xl">
                      {selectedRound.roundCode === "GROUP_STAGE"
                        ? t("picks.round.groupStage")
                        : selectedRound.roundName}
                    </h2>
                  </div>
                </div>
                <div className="picksRoundHeaderActions flex shrink-0 items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => setShowPointSystem(true)}
                    disabled={!canShowPointSystem}
                    className="px-3 py-1.5 text-sm font-semibold shadow-sm sm:px-4 sm:text-base"
                  >
                    {t("picks.actions.pointSystem")}
                  </Button>
                  {predictions.size > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleResetScenario}
                      className="border border-border-subtle bg-background px-3 py-1.5 text-foreground-muted hover:bg-background-raised hover:text-foreground"
                    >
                      {t("picks.actions.reset")}
                    </Button>
                  )}
                </div>
              </div>
              <PicksRoundFilter
                value={selectedRoundCode}
                options={roundFilterOptions}
                onChange={handleRoundFilterChange}
                onAttemptLockedOption={handleLockedRoundAttempt}
              />
            </div>
          )}
          {showWorldCupGroupStagePanels && (
            <section className="space-y-3">
              <div className="rounded-2xl border border-border-subtle bg-background-card p-3 md:p-4">
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">
                      {t("picks.worldCup.qualifierPicks")}
                    </p>
                  </div>
                  {worldCupGroupStageSummary && (
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-border-subtle bg-background-raised px-3 py-1 text-xs font-semibold text-foreground-muted">
                        {t("picks.worldCup.topTwoProgress", {
                          count: worldCupGroupStageSummary.autoQualifierCount,
                          total: WORLD_CUP_ALL_GROUPS.length * WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP,
                        })}
                      </span>
                      <span className="rounded-full border border-border-subtle bg-background-raised px-3 py-1 text-xs font-semibold text-foreground-muted">
                        {t("picks.worldCup.bestThirdProgress", {
                          count: worldCupGroupStageSummary.bestThirdCount,
                          total: WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS,
                        })}
                      </span>
                    </div>
                  )}
                </div>
                    <WorldCupBestThirdOrderTray
                      qualifications={worldCupBestThirdOrder}
                      collapsed={worldCupBestThirdTrayCollapsed}
                      onToggleCollapsed={handleWorldCupBestThirdTrayToggle}
                      onMove={handleWorldCupBestThirdMove}
                      t={t}
                    />
                <WorldCupTeamFilter
                  selectedGroup={worldCupGroupFilter}
                  onSelectedGroupChange={setWorldCupGroupFilter}
                  t={t}
                />
              </div>

              {worldCupGroupStagePanels.map((panel) => (
                <WorldCupGroupStagePanelCard
                  key={panel.group}
                  panel={panel}
                  predictions={predictions}
                  teamScenarioPoints={teamScenarioPoints}
                  onToggleTeam={handleWorldCupGroupStageToggle}
                  onOpenOrderForm={onOpenOrderForm}
                  t={t}
                />
              ))}
            </section>
          )}
          {selectedBracketTreeRounds.length > 0 && (
            <BracketTree
              rounds={selectedBracketTreeRounds}
              competitionCode={competitionCode}
              scenarioMode={scenarioMode}
              predictions={predictions}
              onPredictionChange={handlePredictionChange}
              teamScenarioPoints={teamScenarioPoints}
              pointsPerRound={pointsPerRound}
              onOpenOrderForm={onOpenOrderForm}
            />
          )}
        </section>
      )}
      <InfoModal
        isOpen={showPointSystem}
        onClose={() => setShowPointSystem(false)}
        title={t("picks.pointSystem.title")}
      >
        <div className="space-y-4">
          <div className="rounded-md border border-border-subtle bg-background px-3 py-2">
            <p className="text-body-xs uppercase tracking-[0.14em] text-foreground-muted">{competitionLabel}</p>
            <p className="text-body-sm text-foreground-muted">
              {isSoccerPointSystem ? t("picks.pointSystem.summary.soccer") : t("picks.pointSystem.summary.standard")}
            </p>
          </div>
          <div className="space-y-2">
            {isSoccerPointSystem ? (
              <div className="overflow-hidden rounded-[14px] border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.04)]">
                {soccerSettlementRows.map((row) => {
                  const rowClassNames = getSoccerSettlementRowClassNames(row.key)
                  return (
                    <div key={row.key}>
                      {row.key === "eliminated_semi_final" ? (
                        <div className="flex min-h-[24px] items-center border-y border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.02)] px-4 py-1.5">
                          <p className="text-[9px] font-bold uppercase tracking-[1px] text-[rgba(255,255,255,0.2)]">
                            {t("picks.pointSystem.soccer.eliminatedHeader")}
                          </p>
                        </div>
                      ) : null}
                      <div className={rowClassNames.rowClassName} style={rowClassNames.style}>
                        <div className="flex min-w-0 items-center gap-2.5">
                          {rowClassNames.rankIcon ? (
                            <span
                              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-[16px] font-bold leading-none ${rowClassNames.rankIcon.className}`}
                              aria-hidden
                            >
                              {rowClassNames.rankIcon.kind === "crown" ? (
                                <Crown className="h-5 w-5" />
                              ) : (
                                rowClassNames.rankIcon.label
                              )}
                            </span>
                          ) : null}
                          <p className={rowClassNames.labelClassName}>
                            {getSoccerSettlementDisplayLabel(row.key, t)}
                          </p>
                        </div>
                        <p className={rowClassNames.pointsClassName}>
                          {formatPointValue(row.points)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <>
                {!isSoccerPointSystem && scoringSummaryRows.map((row) => (
                  <article key={row.roundCode} className="rounded-md border border-warning/35 bg-background p-3">
                    <header className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-body font-semibold text-foreground">{row.roundName}</p>
                      <p className="text-body-xs text-foreground-muted">{formatSeriesLabel(row.seriesFormatName, row.winsRequired, t)}</p>
                    </header>
                    <div
                      className="grid gap-2"
                      style={{
                        gridTemplateColumns:
                          row.gameWinPoints === 0
                            ? "repeat(2, minmax(0, 1fr))"
                            : "repeat(3, minmax(0, 1fr))",
                      }}
                    >
                      {row.gameWinPoints > 0 && (
                        <div className="rounded border border-border-subtle bg-background-raised/70 px-2 py-1.5">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-muted">{t("picks.pointSystem.gameWin")}</p>
                          <p className="text-body-sm font-semibold text-foreground">+{formatPointValue(row.gameWinPoints)}</p>
                        </div>
                      )}
                      <div className="rounded border border-border-subtle bg-background-raised/70 px-2 py-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-muted">{t("picks.pointSystem.roundWin")}</p>
                        <p className="text-body-sm font-semibold text-foreground">+{formatPointValue(row.roundWinPoints)}</p>
                      </div>
                      <div className="rounded border border-warning/35 bg-warning/10 px-2 py-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-muted">{t("picks.pointSystem.maxRound")}</p>
                        <p className="text-body-sm font-bold text-brand">{formatPointValue(row.maxRoundPoints)}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </>
            )}
            <div className="flex justify-center pt-4">
              <img
                src="/img/always-100.jpg"
                alt={t("picks.pointSystem.imageAlt")}
                className="w-1/2 rounded-lg border border-[rgba(255,255,255,0.06)] object-contain"
              />
            </div>
          </div>
        </div>
      </InfoModal>
    </div>
  )
}
